import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, LogOut, ArrowLeft, Save, Loader2, DollarSign, CreditCard, Settings, Eye, EyeOff, AlertCircle, Mail, Clock, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';
import AdminPlanManager from '../components/AdminPlanManager';
import { AdminCreditSettings } from '../components/AdminCreditSettings';
import AdminDemoMode from '../components/AdminDemoMode';
import AdminWhatsAppSettings from '../components/AdminWhatsAppSettings';

export default function AdminSettings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showStripeKey, setShowStripeKey] = useState(false);
  
  const [settings, setSettings] = useState({
    tradesperson_monetization_enabled: false,
    stripe_publishable_key: '',
    stripe_secret_key: '',
    platform_name: 'OnsiteHand',
    support_email: 'support@onsitehand.com',
    currency: 'gbp',
    weekly_report_enabled: true,
    weekly_report_day: 'monday',
    weekly_report_time: '09:00'
  });
  const [sendingReports, setSendingReports] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      
      if (response.data.user_type !== 'admin') {
        navigate('/login');
        return;
      }
      
      setUser(response.data);
      fetchSettings();
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/settings`, {
        withCredentials: true
      });
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      // Settings might not exist yet, use defaults
      // Fallback to defaults
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/settings`, settings, {
        withCredentials: true
      });
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="text-slate-600 hover:text-slate-900"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <Building2 className="h-6 w-6 text-orange-500" data-testid="logo" />
              <h1 className="text-xl font-bold text-slate-900">App Settings</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600" data-testid="user-name">Hi, {user?.name}</span>
            <button
              onClick={handleLogout}
              className="btn-secondary text-sm flex items-center gap-2"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Pricing Settings - Inline Plan Manager */}
          <div className="card" data-testid="pricing-section">
            <AdminPlanManager />
          </div>

          {/* Credit Settings - Costs, Top-Up Packages, Founder Bonus */}
          <div className="card" data-testid="credit-settings-section">
            <AdminCreditSettings />
          </div>

          {/* Demo Mode */}
          <div className="card" data-testid="demo-mode-card">
            <AdminDemoMode />
          </div>

          {/* WhatsApp Support Settings */}
          <AdminWhatsAppSettings />

          {/* Tradesperson Monetization */}
          <div className="card" data-testid="monetization-section">
            <div className="mt-0">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-slate-900">Tradesperson Monetization</h3>
                  <p className="text-sm text-slate-500">Enable premium features for tradespeople</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${settings.tradesperson_monetization_enabled ? 'text-green-600' : 'text-slate-500'}`}>
                    {settings.tradesperson_monetization_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleChange('tradesperson_monetization_enabled', !settings.tradesperson_monetization_enabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.tradesperson_monetization_enabled ? 'bg-green-500' : 'bg-slate-300'
                    }`}
                    data-testid="tradesperson-monetization-toggle"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.tradesperson_monetization_enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
              
              {!settings.tradesperson_monetization_enabled && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="text-sm text-slate-600">
                    Tradespeople can currently use the platform for free. Enable this to offer premium features.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Payment Settings */}
          <div className="card" data-testid="payment-section">
            <div className="flex items-center gap-2 mb-6">
              <CreditCard className="h-5 w-5 text-orange-500" />
              <h2 className="text-xl font-bold text-slate-900">Payment Configuration</h2>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex gap-2">
                <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-yellow-900">Important</p>
                  <p className="text-sm text-yellow-700">
                    Stripe API keys are sensitive. Only update these if you want to change where payments are processed.
                    Get your keys from the <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline">Stripe Dashboard</a>.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Currency
                </label>
                <select
                  value={settings.currency}
                  onChange={(e) => handleChange('currency', e.target.value)}
                  className="w-full"
                  data-testid="currency-select"
                >
                  <option value="gbp">GBP (£)</option>
                  <option value="usd">USD ($)</option>
                  <option value="eur">EUR (€)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Stripe Publishable Key
                </label>
                <input
                  type="text"
                  value={settings.stripe_publishable_key}
                  onChange={(e) => handleChange('stripe_publishable_key', e.target.value)}
                  placeholder="pk_live_..."
                  className="w-full font-mono text-sm"
                  data-testid="stripe-publishable-key"
                />
                <p className="text-xs text-slate-500 mt-1">Starts with pk_live_ or pk_test_</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Stripe Secret Key
                </label>
                <div className="relative">
                  <input
                    type={showStripeKey ? 'text' : 'password'}
                    value={settings.stripe_secret_key}
                    onChange={(e) => handleChange('stripe_secret_key', e.target.value)}
                    placeholder="sk_live_..."
                    className="w-full font-mono text-sm pr-10"
                    data-testid="stripe-secret-key"
                  />
                  <button
                    type="button"
                    onClick={() => setShowStripeKey(!showStripeKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="toggle-key-visibility"
                  >
                    {showStripeKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Starts with sk_live_ or sk_test_</p>
              </div>
            </div>
          </div>

          {/* General Settings */}
          <div className="card" data-testid="general-section">
            <div className="flex items-center gap-2 mb-6">
              <Settings className="h-5 w-5 text-orange-500" />
              <h2 className="text-xl font-bold text-slate-900">General Settings</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Platform Name
                </label>
                <input
                  type="text"
                  value={settings.platform_name}
                  onChange={(e) => handleChange('platform_name', e.target.value)}
                  className="w-full"
                  data-testid="platform-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Support Email
                </label>
                <input
                  type="email"
                  value={settings.support_email}
                  onChange={(e) => handleChange('support_email', e.target.value)}
                  className="w-full"
                  data-testid="support-email"
                />
              </div>
            </div>
          </div>

          {/* Email Report Settings */}
          <div className="card" data-testid="email-reports-section">
            <div className="flex items-center gap-2 mb-6">
              <Mail className="h-5 w-5 text-orange-500" />
              <h2 className="text-xl font-bold text-slate-900">Weekly Email Reports</h2>
            </div>
            
            <p className="text-slate-600 mb-6">
              Automated weekly activity reports are sent to employers summarizing their searches, messages, and hires.
            </p>
            
            <div className="flex items-center justify-between mb-6 pb-6 border-b border-slate-200">
              <div>
                <h3 className="font-semibold text-slate-900">Enable Weekly Reports</h3>
                <p className="text-sm text-slate-500">Send weekly activity summaries to employers</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-sm ${settings.weekly_report_enabled ? 'text-green-600' : 'text-slate-500'}`}>
                  {settings.weekly_report_enabled ? 'Enabled' : 'Disabled'}
                </span>
                <button
                  type="button"
                  onClick={() => handleChange('weekly_report_enabled', !settings.weekly_report_enabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.weekly_report_enabled ? 'bg-green-500' : 'bg-slate-300'
                  }`}
                  data-testid="weekly-report-toggle"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.weekly_report_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            
            {settings.weekly_report_enabled && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      <Clock className="h-4 w-4 inline mr-1" />
                      Send Day
                    </label>
                    <select
                      value={settings.weekly_report_day}
                      onChange={(e) => handleChange('weekly_report_day', e.target.value)}
                      className="w-full"
                      data-testid="report-day-select"
                    >
                      <option value="monday">Monday</option>
                      <option value="tuesday">Tuesday</option>
                      <option value="wednesday">Wednesday</option>
                      <option value="thursday">Thursday</option>
                      <option value="friday">Friday</option>
                      <option value="saturday">Saturday</option>
                      <option value="sunday">Sunday</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      <Clock className="h-4 w-4 inline mr-1" />
                      Send Time
                    </label>
                    <input
                      type="time"
                      value={settings.weekly_report_time}
                      onChange={(e) => handleChange('weekly_report_time', e.target.value)}
                      className="w-full"
                      data-testid="report-time-input"
                    />
                  </div>
                </div>
                
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-2">Manual Trigger</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    Send weekly reports to all employers immediately (useful for testing or off-schedule sends).
                  </p>
                  <button
                    onClick={async () => {
                      setSendingReports(true);
                      try {
                        const response = await axios.post(`${BACKEND_URL}/api/admin/send-weekly-reports`, {}, {
                          withCredentials: true
                        });
                        toast.success(`Sent ${response.data.reports_sent} reports to ${response.data.total_employers} employers`);
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to send reports');
                      } finally {
                        setSendingReports(false);
                      }
                    }}
                    disabled={sendingReports}
                    className="btn-secondary flex items-center gap-2"
                    data-testid="send-reports-btn"
                  >
                    {sendingReports ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Send Reports Now
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary flex items-center gap-2 px-6"
              data-testid="save-settings-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
